import React, { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Dialog, DialogTitle, DialogContent, DialogActions, Grid, keyframes, Stack, Divider, Card, CardContent, Radio, RadioGroup, TextField, Button, Box, Typography, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import Swal from 'sweetalert2';
import api from 'core/interceptors/api';
import { Chip } from "@mui/material";
import { Cancel, Visibility, VisibilityOff } from "@mui/icons-material";
import KeywordsInput from 'layouts/tables/Product_Details/KeywordsInput';
import AddServicevalidationSchema from './add_service_scheme';
import CircularProgress from '@mui/material/CircularProgress';
// MUI Icons
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import NotificationsIcon from '@mui/icons-material/Notifications';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import StarIcon from '@mui/icons-material/Star'; // Added for Premium/Pro plans
import DiamondIcon from '@mui/icons-material/Diamond'; // Added for Enterprise plans
import CloudUploadIcon from '@mui/icons-material/CloudUpload'; // Added for Pro plans (example)


// MUI Components
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Checkbox from '@mui/material/Checkbox';
import ListItemText from '@mui/material/ListItemText';
import FormControlLabel from '@mui/material/FormControlLabel';


const fileUploadLableStyle = {
    marginBottom: '8px',
    display: 'block',
    fontWeight: 'bold',
};

const fileUploadInputStyle = {
    display: 'none',
};

const textFieldStyle = {
    '& .MuiOutlinedInput-root': {
        '& fieldset': {
            borderColor: '#000000', // Darker border color
        },
        '&:hover fieldset': {
            borderColor: '#115293', // Darker border color on hover
        },
        '&.Mui-focused fieldset': {
            borderColor: '#0d47a1', // Darker border color when focused
        },
    },
};

const fadeIn = keyframes`
    from { opacity: 0; transform: scale(0.95); }
    to { opacity: 1; transform: scale(1); }
`;

// Updated FeatureItem to accept custom icon color for more vibrancy
const FeatureItem = ({ icon, text, iconColor }) => (
    <Stack direction="row" alignItems="center" spacing={1.5}>
        {React.cloneElement(icon, { sx: { color: iconColor, fontSize: 'small' } })}
        <Typography variant="body2" color="text.secondary">{text}</Typography>
    </Stack>
);


const AddServiceProviderForm = ({ open, handleClose }) => {
    const [sameAsHome, setSameAsHome] = useState(false);
    const [specializations, setSpecializations] = useState([]);
    const [serviceOptions, setServiceOptions] = useState([]);
    const [duplicateErrors, setDuplicateErrors] = useState({
        email: "",
        mobile_number: "",
    });
    const referredByCode = localStorage.getItem("code");
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [paymentPlans, setPaymentPlans] = useState([]);
    const [couponCode, setCouponCode] = useState('');
    const [couponError, setCouponError] = useState('');
    const [discount, setDiscount] = useState(0);
    const [validCoupon, setValidCoupon] = useState(false);

    useEffect(() => {
        const fetchServiceTypes = async () => {
            try {
                const response = await api.get("/service_management/service_types");
                if (response.data.success) {
                    setServiceOptions(response.data.service_types);
                }
            } catch (error) {
                console.error("Error fetching service types:", error);
            }
        };

        const fetchPaymentPlans = async () => {
            try {
                const res = await api.get("/payment_plans/get");
                const servicePlans = (res.data || []).filter(p => p.plan_type === 'service');
                setPaymentPlans(servicePlans);
            } catch (err) {
                console.error("Failed to fetch payment plans:", err);
                setPaymentPlans([]); // Ensure it's an empty array on error
            }
        };

        fetchServiceTypes();
        fetchPaymentPlans();
    }, []);

    const PaymentPlanCard = ({ plan, isSelected, onClick }) => {
        let cardBgColor = '#ffffff';
        let cardBorderColor = 'grey.300';
        let accentColor = '#607d8b'; // Default grey for undefined plans
        let gradientOverlay = 'none';
        let textColor = 'text.primary';
        let buttonColor = 'primary';
        let featureIconColor = 'action'; // Default for feature icons, will be overridden

        // Assign colors based on plan name keywords
        if (plan.name.toLowerCase().includes('basic')) {
            cardBgColor = '#e0f7fa'; // Light Cyan
            cardBorderColor = '#00bcd4'; // Cyan
            accentColor = '#0097a7'; // Darker Cyan (for price, divider, selected checkmark)
            textColor = '#006064'; // Even darker for plan name text
            gradientOverlay = 'linear-gradient(135deg, rgba(0,188,212,0.1), rgba(0,150,136,0.15))';
            buttonColor = 'info';
            featureIconColor = '#00acc1'; // More vibrant cyan for features
        } else if (plan.name.toLowerCase().includes('standard')) {
            cardBgColor = '#e8f5e9'; // Light Green
            cardBorderColor = '#4caf50'; // Green
            accentColor = '#388e3c'; // Darker Green
            textColor = '#1b5e20';
            gradientOverlay = 'linear-gradient(135deg, rgba(76,175,80,0.1), rgba(67,160,71,0.15))';
            buttonColor = 'success';
            featureIconColor = '#66bb6a'; // More vibrant green for features
        } else if (plan.name.toLowerCase().includes('premium') || plan.name.toLowerCase().includes('pro')) {
            cardBgColor = '#fff3e0'; // Light Orange
            cardBorderColor = '#ff9800'; // Orange
            accentColor = '#f57c00'; // Darker Orange
            textColor = '#e65100';
            gradientOverlay = 'linear-gradient(135deg, rgba(255,152,0,0.1), rgba(255,112,67,0.15))';
            buttonColor = 'warning';
            featureIconColor = '#ffb300'; // More vibrant orange for features
        } else if (plan.name.toLowerCase().includes('enterprise')) {
            cardBgColor = '#ede7f6'; // Light Purple
            cardBorderColor = '#673ab7'; // Deep Purple
            accentColor = '#5e35b1'; // Darker Deep Purple
            textColor = '#311b92';
            gradientOverlay = 'linear-gradient(135deg, rgba(103,58,183,0.1), rgba(81,45,168,0.15))';
            buttonColor = 'secondary';
            featureIconColor = '#9c27b0'; // More vibrant purple for features
        }

        const selectedShadow = `0 8px 25px ${accentColor}80, 0 0 0 3px ${accentColor}`;
        const hoverShadow = '0 4px 15px rgba(0,0,0,0.25)';

        return (
            <Card
                variant="outlined"
                onClick={onClick}
                sx={{
                    borderColor: isSelected ? accentColor : cardBorderColor,
                    boxShadow: isSelected ? selectedShadow : '0 2px 8px rgba(0,0,0,0.1)',
                    borderWidth: isSelected ? 3 : 1,
                    cursor: 'pointer',
                    position: 'relative',
                    transition: 'all 0.3s ease-in-out',
                    backgroundColor: isSelected ? cardBgColor : '#ffffff',
                    backgroundImage: gradientOverlay,
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    '&:hover': {
                        boxShadow: hoverShadow,
                        borderColor: accentColor,
                        transform: 'translateY(-5px)',
                    },
                }}
            >
                {isSelected && (
                    <CheckCircleIcon sx={{
                        position: 'absolute',
                        top: 12,
                        right: 12,
                        color: accentColor,
                        fontSize: 30,
                        animation: `${fadeIn} 0.3s ease-in-out`,
                        zIndex: 1,
                    }} />
                )}
                <CardContent sx={{ p: 2.5, display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                    <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="h5" component="div" sx={{ color: textColor, fontWeight: 'bold' }}>{plan.name}</Typography>
                        <Typography sx={{ my: 1, fontSize: 13, color: 'text.secondary' }}>{plan.description}</Typography>
                        <Divider sx={{ my: 2, borderColor: accentColor + '60' }} />
                        <Stack spacing={1.5} my={2}>

                            <FeatureItem icon={<CalendarMonthIcon />} text={`${plan.period} Plan`} iconColor={featureIconColor} />
                            {/* Conditional features based on plan name (example, adjust as needed) */}
                            {plan.name.toLowerCase().includes('premium') && <FeatureItem icon={<StarIcon />} text="Priority Support" iconColor="gold" />}
                            {plan.name.toLowerCase().includes('enterprise') && <FeatureItem icon={<DiamondIcon />} text="Custom Features & Analytics" iconColor="teal" />}
                            {plan.name.toLowerCase().includes('pro') && <FeatureItem icon={<CloudUploadIcon />} text="Unlimited Storage" iconColor="#42a5f5" />}
                            {plan.name.toLowerCase().includes('standard') && <FeatureItem icon={<CloudUploadIcon />} text="Basic Reporting" iconColor="#dce775" />}
                        </Stack>
                    </Box>
                    <Box sx={{ textAlign: 'center', mt: 2 }}>
                        <Typography variant="h4" fontWeight="bold" sx={{ color: accentColor }}>
                            ₹{plan.amount}
                        </Typography>
                        {/* Removed the '/year' or '/month' part as requested previously */}
                        <MDButton
                            variant="contained"
                            color={buttonColor}
                            sx={{ mt: 2 }}
                            onClick={onClick}
                        >
                            {isSelected ? "Selected" : "Choose Plan"}
                        </MDButton>
                    </Box>
                </CardContent>
            </Card>
        );
    };

    const formik = useFormik({
        initialValues: {
            owner_name: '',
            email: '',
            password: '',
            referred_by_code: referredByCode || '',
            keywords: [],
            services_type: [],
            subservices: [],
            additional_details: '',
            worklocation: '',
            homeaddress: '',
            mobile_number: '',
            experiance: '',
            account_no: '',
            ifsc_code: '',
            passbook_name: '',
            bank_name: '',
            branch_name: '',
            pan_card_number: '',
            availabiledays: [],
            profile_photo: null,
            idproof_photo: [],
            experience_certificates: [],
            work_place_gps: null,
            approved_status: "Pending",
            payment_plan_id: '',
            // Removed no_of_notifications and no_of_images_upload from initialValues
        },
        validationSchema: AddServicevalidationSchema,
        onSubmit: async (values) => {
            setLoading(true); // Set loading to true at the beginning of submission

            try {
                const selectedPlan = paymentPlans.find(p => p.id.toString() === values.payment_plan_id);
                if (!selectedPlan) {
                    Swal.fire("Plan Error", "Please select a valid payment plan", "error");
                    setLoading(false); // Reset loading if no plan selected
                    return;
                }

                // Format subservices for saving
                const formattedSpecializations = {};
                if (Array.isArray(values.services_type) && values.services_type.length > 0) {
                    values.services_type.forEach((serviceType) => {
                        const relatedSpecs = specializations
                            .filter((spec) => spec.service_type === serviceType)
                            .flatMap((spec) => spec.service_specializations.split(",").map((s) => s.trim()));
                        const selectedSpecs = values.subservices.filter((subservice) => relatedSpecs.includes(subservice));
                        if (selectedSpecs.length > 0) {
                            formattedSpecializations[serviceType] = [selectedSpecs.join(",")];
                        }
                    });
                }
                // Convert to JSON string only for sending
                const subservicesJson = JSON.stringify(formattedSpecializations);
                const selectedPlanAmount = selectedPlan.amount;
                const payableAmount = validCoupon ? getDiscountedAmount(selectedPlanAmount) : selectedPlanAmount;
                // Initialize Razorpay
                const paymentInit = await api.post("/razor_payments/", {
                    amount_paid: payableAmount,
                    payment_method: "Online",
                    user_id: values.email, // Assuming email is the user ID for payment tracking
                    type_of_vendor: "service_provider",
                });

                const { razorpay_order_id, razorpay_key_id, amount, payment_id } = paymentInit.data;

                const options = {
                    key: razorpay_key_id,
                    amount,
                    currency: "INR",
                    order_id: razorpay_order_id,
                    name: `${selectedPlan.name} Plan`,
                    description: "Subscription Payment for Service Provider",
                    image: "/assets/images/logo.png",
                    handler: async function (response) {
                        setLoading(true); // Re-enable loading during registration after successful payment
                        try {
                            await api.put(`/razor_payments/${payment_id}`, {
                                razorpay_order_id: response.razorpay_order_id,
                                razorpay_payment_id: response.razorpay_payment_id,
                                razorpay_signature: response.razorpay_signature,
                                payment_status: "success",
                                payment_method: "Online",
                                paid_at: new Date().toISOString(),
                                user_id: values.email,
                                type_of_vendor: "service_provider",
                                plan_name: selectedPlan.name,
                                plan_validity_days: selectedPlan.period || selectedPlan.plan_period,
                            });

                            const formData = new FormData();
                            Object.entries(values).forEach(([key, value]) => {
                                // Handle specific fields for FormData
                                if (key === "keywords" && Array.isArray(value)) {
                                    formData.append(key, value.join(","));
                                } else if (key === "services_type" && Array.isArray(value)) {
                                    formData.append(key, value.join(","));
                                } else if (key === "subservices") {
                                    formData.append(key, subservicesJson); // Append the JSON string
                                }
                                else if (key === "work_place_gps") {
                                    if (value instanceof File) {
                                        formData.append(key, value);
                                    }
                                } else if (Array.isArray(value) && (key === "idproof_photo" || key === "experience_certificates")) {
                                    value.forEach(file => formData.append(key, file));
                                } else if (value instanceof File) {
                                    formData.append(key, value);
                                } else {
                                    formData.append(key, value);
                                }
                            });

                            // Add no_of_notifications and no_of_images_upload from selected plan
                            formData.append("no_of_notifications", selectedPlan.no_of_notifications);
                            formData.append("no_of_images_upload", selectedPlan.no_of_images_upload);
                            formData.append("payment_id", payment_id); // Append the payment_id

                            const res = await api.post("/services_register/register_service", formData, {
                                headers: { "Content-Type": "multipart/form-data" },
                            });

                            if (res.data.status === 1) {
                                // Vendor registered successfully
                                try {
                                    // Prepare data needed for incentive earning update
                                    // `res.data` may contain vendor info — adjust if your API returns the registered vendor ID
                                    const referralId = localStorage.getItem('code') || ''; // Replace with actual ID from your API response
                                    const paidAmount = payableAmount;                   // The same paid amount you passed earlier
                                    // Admin info can be from localStorage or your auth context
                                    const adminEmail = localStorage.getItem('email') || '';
                                    const adminRole = localStorage.getItem('role') || '';

                                    // Call incentive earnings API
                                    await api.post('/incentive_earning', {
                                        referral_id: referralId,
                                        paid_amount: paidAmount,
                                        admin_email: adminEmail,
                                        role: adminRole,
                                    });

                                    Swal.fire("Success", "Service Provider registered successfully", "success");
                                } catch (incentiveError) {
                                    console.error("Incentive earnings update failed:", incentiveError);

                                }

                                formik.resetForm();
                                handleClose();
                            } else {
                                Swal.fire("Error", res.data.message || "Service Provider registration failed", "error");
                            }


                        } catch (registrationError) {
                            console.error("Service registration failed after payment:", registrationError);
                            Swal.fire("Error", "Payment successful but registration failed", "error");
                        } finally {
                            setLoading(false); // Ensure loading is reset
                        }
                    },
                    prefill: {
                        name: values.owner_name,
                        contact: values.mobile_number,
                        email: values.email || ""
                    },
                    theme: { color: "#F37254" }
                };

                const rzp = new window.Razorpay(options);

                // Add event listener for when the Razorpay modal is closed by the user
                rzp.on('razorpay.modal.close', () => {
                    console.log("Razorpay modal closed event triggered.");
                    Swal.fire("Payment Cancelled", "The payment process was cancelled.", "info");
                    // No need to set loading to false here, it will be handled by the outer finally
                });
                rzp.open();

            } catch (err) {
                console.error("Error in payment initiation or network issue:", err);
                Swal.fire("Error", err.response?.data?.message || "Failed to initiate payment. Please try again.", "error");
            } finally {
                // This is the key change: Ensure loading is always set to false
                // after the *initial* onSubmit promise settles, whether it resolves
                // with `rzp.open()` or rejects due to an error before that.
                // If the handler is called, it takes over loading, and its finally
                // sets it to false. If the handler is *not* called (e.g., modal closed),
                // this outer finally will catch it.
                setLoading(false);
            }
        }
    });

    const handleClickShowPassword = () => setShowPassword((show) => !show);
    const handleMouseDownPassword = (event) => {
        event.preventDefault();
    };

    const checkDuplicateEmail = async (email) => {
        try {
            const res = await api.post("/services_register/check_duplicate_email", { email });
            return res.data.exists;
        } catch (err) {
            console.error("Email duplicate check failed:", err);
            return false;
        }
    };
    useEffect(() => {
        // Clear discount if coupon is manually erased or payment plan changes
        if (!couponCode || !formik.values.payment_plan_id) {
            setValidCoupon(false);
            setDiscount(0);
        }
    }, [couponCode, formik.values.payment_plan_id]);
    const checkDuplicateMobile = async (mobile_number) => {
        try {
            const res = await api.post("/services_register/check_duplicate_mobile", { mobile_number });
            return res.data.exists;
        } catch (err) {
            console.error("Mobile duplicate check failed:", err);
            return false;
        }
    };
    const validateCoupon = async () => {
        console.log("validateCoupon function started!"); // This confirms button click

        try {
            const res = await api.get("/coupons/"); // Attempt to fetch coupons
            const fetchedCouponList = res.data?.coupons || []; // Store fetched data in a new variable
            console.log("Available Coupons (after fetch):", fetchedCouponList); // Log the *actual* fetched list

            const matched = fetchedCouponList.find(c => c.coupon_id === couponCode.trim().toUpperCase());

            if (!matched) {
                throw new Error("Invalid coupon"); // Throw a specific error for clarity
            }

            const now = new Date();
            const start = new Date(matched.start_date);
            const end = new Date(matched.end_date);

            if (now >= start && now <= end) {
                setDiscount(matched.percentage);
                setValidCoupon(true);

            } else {
                setDiscount(0);
                setValidCoupon(false);

            }
        } catch (err) {
            setDiscount(0);
            setValidCoupon(false);
            console.error("Error during coupon validation or fetch:", err); // Log the full error object!
            let errorMessage = "An unexpected error occurred.";
            if (err.message === "Invalid coupon") {
                errorMessage = "Coupon code not found.";
            } else if (err.response && err.response.data && err.response.data.message) {
                errorMessage = err.response.data.message; // From API response error
            } else if (err.message) {
                errorMessage = err.message; // General JavaScript error message
            }
            Swal.fire("Alert", errorMessage, "info");
        }
    };
    const getDiscountedAmount = (amount) => {
        return Math.round(amount - (amount * discount) / 100);
    };
    const handleCloseAndReset = () => {
        formik.resetForm();
        setDuplicateErrors({ email: "", mobile_number: "" });
        setCouponCode("");
        setDiscount(0);
        setCouponError("");// Reset duplicate errors too
        handleClose();
    };

    const fetchSpecializations = async (selectedServices) => {
        try {
            if (!selectedServices || selectedServices.length === 0) {
                return [];
            }
            const response = await api.post(
                `/service_management/service_specializations/get_specializations`,
                { serviceTypeIds: selectedServices }
            );

            if (response.data.status === 1 && Array.isArray(response.data.service_specializations)) {
                return response.data.service_specializations.map((spec) => ({
                    ...spec,
                    fromAPI: true,
                }));
            } else {
                return [];
            }
        } catch (error) {
            console.error("❌ Fetch Error:", error.response?.data || error.message);
            return [];
        }
    };

    const updateServicesAndSpecializations = async (newSelectedServices) => {
        formik.setFieldValue("services_type", newSelectedServices);

        const fetchedSpecializations = await fetchSpecializations(newSelectedServices);
        setSpecializations(fetchedSpecializations);

        const currentSelectedSubservices = formik.values.subservices;

        if (Array.isArray(currentSelectedSubservices) && currentSelectedSubservices.length > 0) {
            const allValidSpecializationNames = fetchedSpecializations
                .flatMap((spec) => spec.service_specializations.split(","))
                .map(s => s.trim());

            const newSelectedSubservices = currentSelectedSubservices.filter(subservice =>
                allValidSpecializationNames.includes(subservice)
            );
            formik.setFieldValue("subservices", newSelectedSubservices);
        }
    };

    const handleServiceTypeChange = async (event) => {
        const selectedServices = event.target.value;
        updateServicesAndSpecializations(selectedServices);
    };

    const handleRemoveServiceType = (serviceToRemove) => {
        const newSelectedServices = formik.values.services_type.filter(
            (item) => item !== serviceToRemove
        );
        updateServicesAndSpecializations(newSelectedServices);
    };


    const handleFileChange = (event, fieldName) => {
        const files = event.target.files;
        const isMultiple = fieldName === "idproof_photo" || fieldName === "experience_certificates";

        if (files && files.length > 0) {
            const value = isMultiple ? Array.from(files) : files[0];

            formik.setFieldValue(fieldName, value, true); // Set validate to true here
            // formik.setFieldTouched(fieldName, true, false); // This might not be needed if validate is true

            setTimeout(() => {
                formik.validateField(fieldName);
            }, 0);
        } else {
            const resetValue = isMultiple ? [] : null;

            formik.setFieldValue(fieldName, resetValue, true); // Set validate to true here
            // formik.setFieldTouched(fieldName, true, false); // This might not be needed if validate is true

            setTimeout(() => {
                formik.validateField(fieldName);
            }, 0);
        }
    };

    const handleCheckboxChange = (event) => {
        setSameAsHome(event.target.checked);
        if (event.target.checked) {
            formik.setFieldValue('homeaddress', formik.values.worklocation, true); // Validate immediately
        } else {
            if (formik.values.homeaddress === formik.values.worklocation) {
                formik.setFieldValue('homeaddress', '', true); // Validate immediately
            }
        }
    };

    return (
        <Dialog open={open} onClose={handleCloseAndReset} fullWidth maxWidth="lg">
            <DialogTitle>Add Service Provider</DialogTitle>
            <form onSubmit={formik.handleSubmit}>
                <DialogContent>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Box sx={{ backgroundColor: '#e3f2fd', p: 2, borderRadius: 1, mb: 2 }}>
                                <Typography variant="h6" gutterBottom>Basic Information</Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            label="Name"
                                            name="owner_name"
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.owner_name}
                                            fullWidth
                                            margin="normal"
                                            error={formik.touched.owner_name && Boolean(formik.errors.owner_name)}
                                            helperText={formik.touched.owner_name && formik.errors.owner_name}
                                            sx={textFieldStyle}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            label="Email"
                                            name="email"
                                            type="email"
                                            onChange={(e) => {
                                                formik.handleChange(e);
                                                setDuplicateErrors((prev) => ({ ...prev, email: "" }));
                                            }}
                                            onBlur={async (e) => {
                                                formik.handleBlur(e);
                                                const email = e.target.value;
                                                if (email && !formik.errors.email) {
                                                    const isDuplicate = await checkDuplicateEmail(email);
                                                    setDuplicateErrors((prev) => ({
                                                        ...prev,
                                                        email: isDuplicate ? "Email is already registered" : "",
                                                    }));
                                                }
                                            }}
                                            value={formik.values.email}
                                            fullWidth
                                            margin="normal"
                                            error={
                                                (formik.touched.email && Boolean(formik.errors.email)) ||
                                                (formik.touched.email && Boolean(duplicateErrors.email))
                                            }
                                            helperText={
                                                (formik.touched.email && formik.errors.email) ||
                                                (formik.touched.email && duplicateErrors.email)
                                            }
                                            sx={textFieldStyle}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            label="Mobile Number"
                                            name="mobile_number"
                                            type="tel"
                                            onChange={(e) => {
                                                formik.handleChange(e);
                                                setDuplicateErrors((prev) => ({ ...prev, mobile_number: "" }));
                                            }}
                                            onBlur={async (e) => {
                                                formik.handleBlur(e);
                                                const number = e.target.value;
                                                if (number && number.length === 10 && !formik.errors.mobile_number) {
                                                    const isDuplicate = await checkDuplicateMobile(number);
                                                    setDuplicateErrors((prev) => ({
                                                        ...prev,
                                                        mobile_number: isDuplicate ? "Mobile number already registered" : "",
                                                    }));
                                                }
                                            }}
                                            value={formik.values.mobile_number}
                                            fullWidth
                                            margin="normal"
                                            error={
                                                (formik.touched.mobile_number && Boolean(formik.errors.mobile_number)) ||
                                                (formik.touched.mobile_number && Boolean(duplicateErrors.mobile_number))
                                            }
                                            helperText={
                                                (formik.touched.mobile_number && formik.errors.mobile_number) ||
                                                (formik.touched.mobile_number && duplicateErrors.mobile_number)
                                            }
                                            sx={textFieldStyle}
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            label="Password"
                                            name="password"
                                            type={showPassword ? 'text' : 'password'} // Dynamic type
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.password}
                                            fullWidth
                                            margin="normal"
                                            error={formik.touched.password && Boolean(formik.errors.password)}
                                            helperText={formik.touched.password && formik.errors.password}
                                            sx={textFieldStyle}
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            aria-label="toggle password visibility"
                                                            onClick={handleClickShowPassword}
                                                            onMouseDown={handleMouseDownPassword}
                                                            edge="end"
                                                        >
                                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                                        </IconButton>
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                            </Box>
                        </Grid>

                        <Grid item xs={12}>
                            <Box sx={{ backgroundColor: '#f3e5f5', p: 2, borderRadius: 1, mb: 2 }}>
                                <Typography variant="h6" gutterBottom>Service Details</Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6} mt={1}>
                                        <MDBox>
                                            <FormControl fullWidth>
                                                <InputLabel id="service-type-label">Service Types</InputLabel>
                                                <Select
                                                    labelId="service-type-label"
                                                    name="services_type"
                                                    value={formik.values.services_type}
                                                    onChange={handleServiceTypeChange}
                                                    multiple
                                                    label="Service Types"
                                                    renderValue={(selected) => (
                                                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, p: 1 }}>
                                                            {selected.map((value) => (
                                                                <Chip key={value} label={value} onDelete={() => handleRemoveServiceType(value)} deleteIcon={<Cancel fontSize="small" />} />
                                                            ))}
                                                        </Box>
                                                    )}
                                                    sx={{ height: "auto", minHeight: 45 }}
                                                    fullWidth
                                                >
                                                    {serviceOptions.map((type) => (
                                                        <MenuItem key={type.id} value={type.service_type}>
                                                            {type.service_type}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                                {formik.touched.services_type && formik.errors.services_type && (
                                                    <Typography color="error" variant="body2">{formik.errors.services_type}</Typography>
                                                )}
                                            </FormControl>
                                        </MDBox>
                                    </Grid>
                                    <Grid item xs={12} sm={6} mt={1}>
                                        <FormControl fullWidth>
                                            <InputLabel id="specializations-label">Specializations</InputLabel>
                                            <Select
                                                labelId="specializations-label"
                                                multiple
                                                label="Specializations"
                                                value={Array.isArray(formik.values.subservices) ? formik.values.subservices : []}
                                                onChange={(event) => {
                                                    const selected = event.target.value;
                                                    formik.setFieldValue("subservices", Array.isArray(selected) ? selected : [], true); // Validate immediately
                                                }}
                                                renderValue={(selected) =>
                                                    Array.isArray(selected) && selected.length > 0 ? selected.join(", ") : "Select Specializations"
                                                }
                                                sx={{ height: "auto", minHeight: 42, maxHeight: 250, overflowY: "auto" }}
                                            >
                                                {specializations
                                                    .flatMap((spec) => spec.service_specializations.split(","))
                                                    .map((spec, index) => (
                                                        <MenuItem key={index} value={spec.trim()} sx={{ py: 0.5, fontSize: 14 }} >
                                                            <Checkbox checked={(formik.values.subservices || []).includes(spec.trim())} size="small" />
                                                            <ListItemText primary={spec.trim()} primaryTypographyProps={{ fontSize: 14 }} />
                                                        </MenuItem>
                                                    ))}
                                            </Select>
                                            {formik.touched.subservices && formik.errors.subservices && (
                                                <Typography color="error" variant="body2">{formik.errors.subservices}</Typography>
                                            )}
                                        </FormControl>

                                        <Box mt={2} display="flex" flexWrap="wrap" gap={1}>
                                            {(Array.isArray(formik.values.subservices) ? formik.values.subservices : []).map((spec, index) => (
                                                <Chip
                                                    key={index}
                                                    label={spec}
                                                    onDelete={() =>
                                                        formik.setFieldValue(
                                                            "subservices",
                                                            formik.values.subservices.filter((s) => s !== spec),
                                                            true // Validate immediately
                                                        )
                                                    }
                                                    sx={{ m: 0.5 }}
                                                />
                                            ))}
                                        </Box>
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            label="Shop / Work Address"
                                            name="worklocation"
                                            multiline
                                            rows={4}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.worklocation} // Always show worklocation input value
                                            fullWidth
                                            margin="normal"
                                            error={formik.touched.worklocation && Boolean(formik.errors.worklocation)}
                                            helperText={formik.touched.worklocation && formik.errors.worklocation}
                                            sx={textFieldStyle}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            label="Home Address"
                                            name="homeaddress"
                                            multiline
                                            rows={4}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={sameAsHome ? formik.values.worklocation : formik.values.homeaddress}
                                            fullWidth
                                            margin="normal"
                                            error={formik.touched.homeaddress && Boolean(formik.errors.homeaddress)}
                                            helperText={formik.touched.homeaddress && formik.errors.homeaddress}
                                            sx={textFieldStyle}
                                        />
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={sameAsHome}
                                                    onChange={handleCheckboxChange}
                                                    sx={{
                                                        '& .MuiSvgIcon-root': {
                                                            color: '#f44336',
                                                        },
                                                        '&:hover .MuiSvgIcon-root': {
                                                            color: '#e53935',
                                                        },
                                                        '&.Mui-checked .MuiSvgIcon-root': {
                                                            color: '#1976d2',
                                                        },
                                                    }}
                                                />
                                            }
                                            label="Same as Work Address" // Label changed for clarity
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={6} >
                                        <KeywordsInput
                                            label="Service Related Keywords"
                                            keywords={formik.values.keywords || []}
                                            onChange={(newKeywords) =>
                                                formik.setFieldValue("keywords", newKeywords, true) // Validate immediately
                                            }
                                            error={formik.touched.keywords && Boolean(formik.errors.keywords)}
                                            helperText={formik.touched.keywords && formik.errors.keywords}
                                        />
                                    </Grid>
                                </Grid>
                            </Box>

                            <Box sx={{ backgroundColor: '#e8f5e9', p: 2, borderRadius: 1, mb: 2 }}>
                                <Typography variant="h6" gutterBottom>Bank Details</Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            label="Account Number"
                                            name="account_no"
                                            type="number"
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.account_no}
                                            fullWidth
                                            margin="normal"
                                            error={formik.touched.account_no && Boolean(formik.errors.account_no)}
                                            helperText={formik.touched.account_no && formik.errors.account_no}
                                            sx={textFieldStyle}
                                            inputProps={{ min: 0 }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            label="IFSC Code"
                                            name="ifsc_code"
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.ifsc_code}
                                            fullWidth
                                            margin="normal"
                                            error={formik.touched.ifsc_code && Boolean(formik.errors.ifsc_code)}
                                            helperText={formik.touched.ifsc_code && formik.errors.ifsc_code}
                                            sx={textFieldStyle}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            label="Passbook Name"
                                            name="passbook_name"
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.passbook_name}
                                            fullWidth
                                            margin="normal"
                                            error={formik.touched.passbook_name && Boolean(formik.errors.passbook_name)}
                                            helperText={formik.touched.passbook_name && formik.errors.passbook_name}
                                            sx={textFieldStyle}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            label="Bank Name"
                                            name="bank_name"
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.bank_name}
                                            fullWidth
                                            margin="normal"
                                            error={formik.touched.bank_name && Boolean(formik.errors.bank_name)}
                                            helperText={formik.touched.bank_name && formik.errors.bank_name}
                                            sx={textFieldStyle}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            label="Branch Name"
                                            name="branch_name"
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.branch_name}
                                            fullWidth
                                            margin="normal"
                                            error={formik.touched.branch_name && Boolean(formik.errors.branch_name)}
                                            helperText={formik.touched.branch_name && formik.errors.branch_name}
                                            sx={textFieldStyle}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            label="Number of Years of Experience"
                                            name="experiance"
                                            type="number"
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.experiance}
                                            fullWidth
                                            margin="normal"
                                            error={formik.touched.experiance && Boolean(formik.errors.experiance)}
                                            helperText={formik.touched.experiance && formik.errors.experiance}
                                            sx={textFieldStyle}
                                            inputProps={{ min: 0 }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            label="Additional Details"
                                            name="additional_details"
                                            multiline
                                            rows={4}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.additional_details}
                                            fullWidth
                                            margin="normal"
                                            error={formik.touched.additional_details && Boolean(formik.errors.additional_details)}
                                            helperText={formik.touched.additional_details && formik.errors.additional_details}
                                            sx={textFieldStyle}
                                        />
                                    </Grid>
                                </Grid>
                            </Box>

                            <Box sx={{ backgroundColor: '#fff3e0', p: 2, borderRadius: 1, mb: 2 }}>
                                <Typography variant="h6" gutterBottom>Identification Details</Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            label="PAN Card"
                                            name="pan_card_number"
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.pan_card_number}
                                            fullWidth
                                            margin="normal"
                                            error={formik.touched.pan_card_number && Boolean(formik.errors.pan_card_number)}
                                            helperText={formik.touched.pan_card_number && formik.errors.pan_card_number}
                                            sx={textFieldStyle}
                                        />
                                    </Grid>
                                </Grid>
                            </Box>

                            <Box sx={{ backgroundColor: '#fce4ec', p: 2, borderRadius: 1 }}>
                                <Typography variant="h6" gutterBottom>Upload Documents</Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <MDBox margin="normal" fullWidth>
                                            <InputLabel htmlFor="profile_photo" style={fileUploadLableStyle}>Profile Photo </InputLabel>
                                            <MDButton component="label" color="warning">
                                                Upload Profile Photo
                                                <input
                                                    type="file"
                                                    hidden
                                                    id="profile_photo"
                                                    name="profile_photo"
                                                    onChange={(event) => handleFileChange(event, 'profile_photo')}
                                                    accept="image/*"
                                                />
                                            </MDButton>
                                        </MDBox>
                                        {formik.values.profile_photo && (
                                            <MDBox display="flex" flexDirection="row" gap={2} mt={2}>
                                                <MDBox component="img"
                                                    src={URL.createObjectURL(formik.values.profile_photo)}
                                                    alt="Profile Photo"
                                                    width="100px"
                                                    height="100px"
                                                    style={{ objectFit: 'cover' }}
                                                />
                                            </MDBox>
                                        )}
                                        {formik.touched.profile_photo && formik.errors.profile_photo && (
                                            <Typography variant="body2" color="error">
                                                {formik.errors.profile_photo}
                                            </Typography>
                                        )}
                                    </Grid>

                                    <Grid item xs={12} sm={6}>
                                        <MDBox margin="normal" fullWidth>
                                            <InputLabel htmlFor="work_place_gps" style={fileUploadLableStyle}>Work Place GPS [Optional]</InputLabel>
                                            <MDButton
                                                component="label"
                                                color="warning"
                                            >
                                                Upload Work GPS Photo
                                                <input
                                                    type="file"
                                                    hidden
                                                    id="work_place_gps"
                                                    name="work_place_gps"
                                                    onChange={(event) => handleFileChange(event, 'work_place_gps')}
                                                    accept="image/*"
                                                />
                                            </MDButton>
                                        </MDBox>
                                        {formik.values.work_place_gps && (
                                            <MDBox display="flex" flexDirection="row" gap={2} mt={2}>
                                                <MDBox component="img"
                                                    src={URL.createObjectURL(formik.values.work_place_gps)}
                                                    alt="Work GPS Photo"
                                                    width="100px"
                                                    height="100px"
                                                    style={{ objectFit: 'cover' }}
                                                />
                                            </MDBox>
                                        )}
                                        {formik.touched.work_place_gps && formik.errors.work_place_gps && (
                                            <Typography variant="body2" color="error">
                                                {formik.errors.work_place_gps}
                                            </Typography>
                                        )}
                                    </Grid>

                                    <Grid item xs={12} sm={6}>
                                        <MDBox margin="normal" fullWidth>
                                            <InputLabel htmlFor="idproof_photo" style={fileUploadLableStyle}>Gov ID Proof [Front & Back]</InputLabel>
                                            <MDButton
                                                variant="contained"
                                                component="label"
                                                color="warning"
                                            >
                                                Upload ID Proof Photo
                                                <input
                                                    type="file"
                                                    hidden
                                                    id="idproof_photo"
                                                    name="idproof_photo"
                                                    onChange={(event) => handleFileChange(event, 'idproof_photo')}
                                                    accept="image/*"
                                                    multiple
                                                />
                                            </MDButton>
                                        </MDBox>
                                        {formik.values.idproof_photo && formik.values.idproof_photo.length > 0 && (
                                            <MDBox display="flex" flexDirection="row" gap={2} mt={2}>
                                                {formik.values.idproof_photo.map((file, index) => {
                                                    if (file && file instanceof Blob) {
                                                        return (
                                                            <MDBox key={index} component="img"
                                                                src={URL.createObjectURL(file)}
                                                                alt={`Gov ID Proof Photo ${index + 1}`}
                                                                width="100px"
                                                                height="100px"
                                                                style={{ objectFit: 'cover' }}
                                                            />
                                                        );
                                                    } else {
                                                        console.error('Invalid file detected:', file);
                                                        return null;
                                                    }
                                                })}
                                            </MDBox>
                                        )}
                                        {formik.touched.idproof_photo && formik.errors.idproof_photo && (
                                            <Typography variant="body2" color="error">
                                                {formik.errors.idproof_photo}
                                            </Typography>
                                        )}
                                    </Grid>

                                    <Grid item xs={12} sm={6}>
                                        <MDBox margin="normal" fullWidth>
                                            <InputLabel htmlFor="experience_certificates" style={fileUploadLableStyle}>Experience Photos</InputLabel>
                                            <MDButton
                                                variant="contained"
                                                component="label"
                                                color="warning"
                                            >
                                                Upload Experience Photos
                                                <input
                                                    type="file"
                                                    hidden
                                                    id="experience_certificates"
                                                    name="experience_certificates"
                                                    onChange={(event) => handleFileChange(event, 'experience_certificates')}
                                                    accept="image/*"
                                                    multiple
                                                />
                                            </MDButton>
                                        </MDBox>

                                        {formik.values.experience_certificates && formik.values.experience_certificates.length > 0 && (
                                            <MDBox display="flex" flexDirection="row" gap={2} mt={2}>
                                                {formik.values.experience_certificates.map((file, index) => {
                                                    if (file && file instanceof Blob) {
                                                        return (
                                                            <MDBox key={index} component="img"
                                                                src={URL.createObjectURL(file)}
                                                                alt={`Experience Photo ${index + 1}`}
                                                                width="100px"
                                                                height="100px"
                                                                style={{ objectFit: 'cover' }}
                                                            />
                                                        );
                                                    } else {
                                                        console.error('Invalid file detected:', file);
                                                        return null;
                                                    }
                                                })}
                                            </MDBox>
                                        )}
                                        {formik.touched.experience_certificates && formik.errors.experience_certificates && (
                                            <Typography variant="body2" color="error">
                                                {formik.errors.experience_certificates}
                                            </Typography>
                                        )}
                                    </Grid>

                                </Grid>
                                <Grid item xs={12}>
                                    <Box sx={{ bgcolor: '#f3e5f5', p: 2, borderRadius: 2 }}>
                                        <Typography variant="h6" gutterBottom>Select a Payment Plan</Typography>
                                        <Grid container spacing={2}>
                                            {paymentPlans.length > 0 ? (
                                                paymentPlans.map((plan) => (
                                                    <Grid item xs={12} sm={6} md={4} key={plan.id}>
                                                        <PaymentPlanCard
                                                            plan={plan}
                                                            isSelected={formik.values.payment_plan_id === plan.id.toString()}
                                                            onClick={() => formik.setFieldValue("payment_plan_id", plan.id.toString(), true)} // Validate immediately
                                                        />
                                                    </Grid>
                                                ))
                                            ) : (
                                                <Grid item xs={12}><Typography>No Payment Plans Available</Typography></Grid>
                                            )}
                                        </Grid>
                                        {formik.touched.payment_plan_id && formik.errors.payment_plan_id && (
                                            <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block' }}>
                                                {formik.errors.payment_plan_id}
                                            </Typography>
                                        )}
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        label="Coupon Code"
                                        name="coupon_code"
                                        value={couponCode}
                                        onChange={(e) => setCouponCode(e.target.value)}
                                        fullWidth
                                        margin="normal"
                                        error={!!couponError}
                                        helperText={couponError}
                                        // Disable until a payment plan is selected
                                        disabled={!formik.values.payment_plan_id}
                                    />
                                    <Button
                                        variant="contained"
                                        color="info"
                                        onClick={validateCoupon}
                                        // Disable when loading, if code empty, or no plan selected
                                        disabled={loading || !couponCode || !formik.values.payment_plan_id}
                                        sx={{ mt: 1, ml: 2 }}
                                    >
                                        Apply
                                    </Button>

                                    {/* Discount message and discounted amount */}
                                    {validCoupon && formik.values.payment_plan_id && (
                                        <>
                                            <Typography color="success.main" mt={1}>
                                                Coupon Applied: {discount}% off
                                            </Typography>
                                            <Typography mt={1} fontWeight={700}>
                                                Discounted Amount:&nbsp;
                                                ₹
                                                {getDiscountedAmount(
                                                    paymentPlans.find(p => p.id.toString() === formik.values.payment_plan_id)?.amount || 0
                                                )}
                                            </Typography>
                                        </>
                                    )}
                                </Grid>
                            </Box>
                        </Grid>
                    </Grid>
                </DialogContent>

                <DialogActions>
                    <Button onClick={handleCloseAndReset} color="primary">
                        Cancel
                    </Button>
                    <MDButton
                        type="submit"
                        color="success"
                        disabled={
                            loading ||
                            !formik.isValid ||
                            !formik.dirty ||
                            !!duplicateErrors.email ||
                            !!duplicateErrors.mobile_number ||
                            !formik.values.payment_plan_id // Ensure a plan is selected
                        }

                    >
                        {loading ? (
                            <CircularProgress size={24} color="inherit" />
                        ) : (
                            "Save"
                        )}
                    </MDButton>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default AddServiceProviderForm;